import {
  memo,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useSettingsPresentation } from "../settings-page-layout/settings-presentation-context";
import {
  SETTINGS_SEARCH_QUERY_PARAM,
  settingsSearchTextMatches,
} from "./settings-search";
import * as css from "./settings-search-section.module.css";

interface SettingsSearchSectionProps {
  children: ReactNode;
  searchTerms?: readonly string[];
}

export const SettingsSearchSection = memo(function SettingsSearchSection(
  props: SettingsSearchSectionProps,
) {
  const redesigned = useSettingsPresentation() === "redesigned";
  return redesigned ? (
    <EnabledSettingsSearchSection {...props} />
  ) : (
    <>{props.children}</>
  );
});

const EnabledSettingsSearchSection = memo(
  function EnabledSettingsSearchSection({
    children,
    searchTerms = [],
  }: SettingsSearchSectionProps) {
    const [searchParams] = useSearchParams();
    const search = searchParams.get(SETTINGS_SEARCH_QUERY_PARAM) ?? "";
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(true);
    const searchTermsText = useMemo(() => searchTerms.join(" "), [searchTerms]);

    useLayoutEffect(() => {
      const updateVisibility = () => {
        const renderedText = elementRef.current?.textContent ?? "";
        setVisible(
          settingsSearchTextMatches({
            search,
            text: `${searchTermsText} ${renderedText}`,
          }),
        );
      };

      updateVisibility();
      const observer = new MutationObserver(updateVisibility);
      if (elementRef.current) {
        observer.observe(elementRef.current, {
          characterData: true,
          childList: true,
          subtree: true,
        });
      }
      return () => observer.disconnect();
    }, [search, searchTermsText]);

    return (
      <div
        className={visible ? css.visible : css.hidden}
        data-settings-search-section="true"
        data-settings-search-visible={visible}
        ref={elementRef}
      >
        {children}
      </div>
    );
  },
);
